import { Tooltip } from '@ibsys/kit';
import { AlertTriangleIcon, SigmaIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { PointsRuleAttention } from '@/entities/expert-report';

type Props = {
  /** Результат считается особым правилом расчёта — не тем, что задано по умолчанию */
  hasCustomPointsRule?: boolean;
  /** Правило расчёта требует внимания; `null` — не требует */
  attention?: PointsRuleAttention | null;
  /** Правило расчёта требует внимания у скрытого внутри узла блока; `null` — не требует */
  nestedAttention?: PointsRuleAttention | null;
};

/** Подсказка отметки «требует внимания» — по тому, чем именно правило разошлось с деревом */
const ATTENTION_HINT_KEY: Record<PointsRuleAttention, 'inapplicablePointsRuleHint' | 'missingChildrenPointsRuleHint'> =
  {
    inapplicable: 'inapplicablePointsRuleHint',
    missingChildren: 'missingChildrenPointsRuleHint',
  };

/** Подсказка отметки о скрытом внутри блоке — называет ту же причину, но у вложенного правила */
const NESTED_ATTENTION_HINT_KEY: Record<
  PointsRuleAttention,
  'nestedInapplicablePointsRuleHint' | 'nestedMissingChildrenPointsRuleHint'
> = {
  inapplicable: 'nestedInapplicablePointsRuleHint',
  missingChildren: 'nestedMissingChildrenPointsRuleHint',
};

/**
 * Отметки правила расчёта в дереве: особое условие оценивания и правило, требующее внимания —
 * как своё, так и правило блока, скрытого внутри свёрнутого узла.
 *
 * Правило владельца в карточке самого вложенного блока не видно, поэтому расхождение правила с
 * деревом блоков показывается прямо в дереве — до того, как оно проявится результатом по диалогу.
 * Об этом же расхождении говорит предупреждение после сохранения. Отметка о скрытом блоке —
 * приглушённая: она говорит не об этом узле, а о том, что расхождение ждёт внутри него.
 */
export const BlockPointsRuleMarks: FC<Props> = ({ hasCustomPointsRule, attention, nestedAttention }) => {
  const { t } = useTranslation('expertReport');

  if (!hasCustomPointsRule && !attention && !nestedAttention) return null;

  return (
    <>
      {hasCustomPointsRule && (
        <Tooltip tooltip={t('customPointsRuleHint')}>
          <SigmaIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        </Tooltip>
      )}
      {attention && (
        <Tooltip tooltip={t(ATTENTION_HINT_KEY[attention])}>
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning-foreground" />
        </Tooltip>
      )}
      {nestedAttention && (
        <Tooltip tooltip={t(NESTED_ATTENTION_HINT_KEY[nestedAttention])}>
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning-foreground/60" />
        </Tooltip>
      )}
    </>
  );
};
